import {
	faArrowRight,
	faCopy,
	faDownload,
	faEdit,
	faFileArchive,
	faFileExport,
	faFolderPlus,
	faLayerGroup,
	faLock,
	faProjectDiagram,
	faRetweet,
	faShieldHalved,
	faTable,
	faTrash,
	faUnlock,
	faWifi,
	faWindowMaximize,
} from '@fortawesome/free-solid-svg-icons'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DialogWrapper } from '@/components'
import {
	ContextMenuItem,
	ContextMenuItems,
} from '@/components/ContextMenu/ContextMenu'
import { getFullySelectedNodes } from '@/components/TreeV2'
import { StructureDto, StructureObjectDto } from '@/endpoints/models'
import {
	useAppContext,
	useAppDispatch,
	useAppStore,
	useAuthContext,
} from '@/hooks'
import { useRunSaveBeforeLeave } from '@/pages/User/pages/Home/context/SaveBeforeLeaveContext'
import { lockNode } from '@/store/modules/node/actions'
import { loadNode } from '@/store/modules/node/general-actions'
import { openTab } from '@/store/modules/tab/actions'
import { logError } from '@/utils/errorHandling'
import {
	canUnlockStructure,
	hasStructureEditPermission,
	hasStructureManageAccessPermission,
	hasStructureViewPermission,
	isLocked,
	isLockedByOther,
	shouldShowUnlockButton,
} from '@/utils/locking'
import {
	isDublicableNodeType,
	isEditableNodeType,
	isImportableNodeType,
} from '@/utils/nodes'

import { useTreePanelOperations } from '../TreePanelContext'
import { AddNodeItem } from './AddNodeItem'
import { ExportStructure } from './ExportStructure'
import {
	batchEditDialog,
	batchUnlockDialog,
	deleteNodeDialog,
	deleteNodesModalDialog,
	duplicateNodeModalDialog,
	importStructureModalDialog,
	massSqlExportModalDialog,
	unlockNodeModalDialog,
} from './ItemMenuDialogs'

type Props = {
	node: StructureDto
	onOpenAcl?: (node: StructureDto) => void
	onRefresh?: () => void
	onRefreshParent?: () => void
}

export const ItemMenu = ({
	node,
	onRefresh,
	onRefreshParent,
	onOpenAcl,
}: Props) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const runSaveBeforeLeave = useRunSaveBeforeLeave()
	const auth = useAuthContext()
	const user = useAppStore((state) => state.auth.user)
	const { refreshSystemsSilent } = useTreePanelOperations()
	/** Structure ACL (`structurePermissions`): VIEW | EDIT | MANAGE_ACCESS | FORCE_UNLOCK — see `@/utils/locking`. */
	const canOpenInTab = hasStructureViewPermission(node)
	const canExportStructure = hasStructureViewPermission(node)
	const canOpenAcl =
		hasStructureManageAccessPermission(node) && node.id != null && !!onOpenAcl
	const canEditStructure = hasStructureEditPermission(node)
	const showUnlock = shouldShowUnlockButton(node, user)

	const { nodes, selectedNodes, selectMode, children } = useAppStore(
		(state) => state.node,
	)

	// Filter out partially selected folders (indeterminate state)
	const fullySelectedNodes = useMemo(
		() =>
			getFullySelectedNodes(
				selectedNodes,
				children as Record<number, number[]>,
				nodes as Record<number, StructureDto>,
			),
		[selectedNodes, children, nodes],
	)

	const open = async () => {
		await runSaveBeforeLeave()
		dispatch(openTab(node, false))
	}
	const lock = async () => {
		await dispatch(lockNode(node))
		refreshSystemsSilent().catch((e) =>
			logError(e, { operation: 'ItemMenu.refreshSystemsSilent' }),
		)
	}

	const [download, setDownload] = useState(false)

	const canDuplicate = canEditStructure && isDublicableNodeType(node.type)

	const canDelete =
		node.type === StructureObjectDto.TypeEnum.SYSTEM
			? auth.canDeleteSystem() &&
				hasStructureEditPermission(node) &&
				!isLockedByOther(node, user)
			: canEditStructure &&
				!isLockedByOther(node, user) &&
				node.type !== StructureObjectDto.TypeEnum.ROOT &&
				node.type !== StructureObjectDto.TypeEnum.FOLDER_WORKFLOW

	const canImport = canEditStructure && isImportableNodeType(node.type)

	// Track which nodes we've already attempted to load to prevent loops
	const loadedNodesRef = useRef<Set<number>>(new Set())

	useEffect(() => {
		const loadMissingNodes = async () => {
			// Find nodes that need to be loaded (not in Redux and not already attempted)
			const nodesToLoad = selectedNodes.filter(
				(nodeId) => !nodes[nodeId] && !loadedNodesRef.current.has(nodeId),
			)

			if (nodesToLoad.length === 0) return

			// Mark these nodes as being loaded before starting
			nodesToLoad.forEach((id) => loadedNodesRef.current.add(id))

			for (const nodeId of nodesToLoad) {
				try {
					await dispatch(loadNode(nodeId))
				} catch (e) {
					logError(e, { operation: 'ItemMenu.loadNodes' })
				}
			}
		}

		loadMissingNodes()
	}, [dispatch, nodes, selectedNodes])

	if (!selectMode) {
		return (
			<ContextMenuItems>
				<ContextMenuItem
					onClick={open}
					icon={faArrowRight}
					role="menuitem"
					title={t('CONTEXT_OPEN_TAB')}
					disabled={!canOpenInTab}
				/>

				{canEditStructure &&
					(node.type === StructureObjectDto.TypeEnum.FOLDER ||
						node.type === StructureObjectDto.TypeEnum.SYSTEM) && (
						<AddNodeItem
							node={node}
							type={StructureObjectDto.TypeEnum.FOLDER}
							label={t('CONTEXT_ADD_FOLDER')}
							icon={faFolderPlus}
							onRefresh={onRefresh}
						/>
					)}

				{canEditStructure &&
					node.type === StructureObjectDto.TypeEnum.FOLDER && (
						<>
							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.TABLE}
								label={t('CONTEXT_ADD_TABLE')}
								icon={faTable}
								onRefresh={onRefresh}
							/>

							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.DIAGRAM}
								label={t('ADD_DIAGRAM')}
								icon={faProjectDiagram}
								onRefresh={onRefresh}
							/>

							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.VIEW}
								label={t('CONTEXT_ADD_VIEW')}
								icon={faWindowMaximize}
								onRefresh={onRefresh}
							/>

							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.API_NODE}
								label={t('CONTEXT_ADD_API_NODE')}
								icon={faWifi}
								onRefresh={onRefresh}
							/>
						</>
					)}

				{canEditStructure &&
					node.type === StructureObjectDto.TypeEnum.API_NODE && (
						<>
							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.API_COLLECTION}
								label={t('CONTEXT_ADD_API_ROWSET')}
								icon={faLayerGroup}
								onRefresh={onRefresh}
							/>

							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.MAPPING_READ}
								label={t('CONTEXT_ADD_MAPPING_READ')}
								icon={faRetweet}
								onRefresh={onRefresh}
							/>

							<AddNodeItem
								node={node}
								type={StructureObjectDto.TypeEnum.MAPPING_WRITE}
								label={t('CONTEXT_ADD_MAPPING_WRITE')}
								icon={faRetweet}
								onRefresh={onRefresh}
							/>
						</>
					)}

				{canEditStructure &&
					node.type === StructureObjectDto.TypeEnum.TABLE && (
						<AddNodeItem
							node={node}
							type={StructureObjectDto.TypeEnum.MAPPING}
							label={t('CONTEXT_ADD_MAPPING')}
							icon={faRetweet}
							onRefresh={onRefresh}
						/>
					)}

				{canEditStructure &&
					node.type === StructureObjectDto.TypeEnum.FOLDER_WORKFLOW && (
						<AddNodeItem
							node={node}
							type={StructureObjectDto.TypeEnum.WORKFLOW}
							label={t('CONTEXT_ADD_WORKFLOW')}
							icon={faProjectDiagram}
							onRefresh={onRefresh}
						/>
					)}

				{canEditStructure && isEditableNodeType(node.type) && (
					<>
						{!isLocked(node) && (
							<ContextMenuItem
								onClick={lock}
								role="menuitem"
								icon={faLock}
								title={t('CONTEXT_LOCK')}
							/>
						)}

						{showUnlock && (
							<DialogWrapper
								dialog={unlockNodeModalDialog}
								dispatch={dispatch}
								node={node}
							>
								{(onClick) => (
									<ContextMenuItem
										onClick={onClick}
										role="menuitem"
										icon={faUnlock}
										title={t('CONTEXT_UNLOCK')}
										disabled={!canUnlockStructure(node, user)}
									/>
								)}
							</DialogWrapper>
						)}
					</>
				)}

				{canDuplicate && (
					<DialogWrapper dialog={duplicateNodeModalDialog} node={node}>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								role="menuitem"
								icon={faCopy}
								title={t('DUPLICATE')}
							/>
						)}
					</DialogWrapper>
				)}

				{canDelete && (
					<DialogWrapper
						dialog={(opened, onClose, node, dispatch, selectedNodes) =>
							deleteNodeDialog(
								opened,
								onClose,
								node,
								dispatch,
								selectedNodes,
								onRefreshParent,
							)
						}
						node={node}
					>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								role="menuitem"
								icon={faTrash}
								title={t('CONTEXT_DELETE')}
							/>
						)}
					</DialogWrapper>
				)}

				{canOpenAcl && (
					<ContextMenuItem
						onClick={() => onOpenAcl?.(node)}
						icon={faShieldHalved}
						role="menuitem"
						title={t('PERMISSIONS')}
					/>
				)}

				<ContextMenuItem
					onClick={() => setDownload(true)}
					icon={faDownload}
					role="menuitem"
					title={t('EXPORT')}
					disabled={!canExportStructure}
				/>

				{download && (
					<ExportStructure
						nodeId={node.id}
						onDownloadFinish={() => setDownload(false)}
					/>
				)}

				{canImport && (
					<DialogWrapper dialog={importStructureModalDialog} node={node}>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								icon={faFileExport}
								role="menuitem"
								title={t('IMPORT')}
							/>
						)}
					</DialogWrapper>
				)}

				{canImport && (
					<DialogWrapper
						dialog={massSqlExportModalDialog}
						node={node}
						selectedNodes={fullySelectedNodes}
					>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								icon={faFileArchive}
								role="menuitem"
								title={t('BATCH_EXPORT')}
								disabled={!canExportStructure}
							/>
						)}
					</DialogWrapper>
				)}
			</ContextMenuItems>
		)
	} else {
		return (
			<ContextMenuItems>
				{canImport && (
					<DialogWrapper
						dialog={massSqlExportModalDialog}
						node={node}
						selectedNodes={fullySelectedNodes}
					>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								icon={faFileArchive}
								role="menuitem"
								title={t('BATCH_EXPORT')}
								disabled={!canExportStructure}
							/>
						)}
					</DialogWrapper>
				)}
				{fullySelectedNodes.length > 0 && (
					<DialogWrapper dialog={deleteNodesModalDialog}>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								icon={faTrash}
								role="menuitem"
								title={t('BATCH_DELETE')}
								disabled={!canEditStructure}
							/>
						)}
					</DialogWrapper>
				)}
				{fullySelectedNodes.length > 0 && (
					<DialogWrapper dialog={batchUnlockDialog} onRefresh={onRefresh}>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								icon={faUnlock}
								role="menuitem"
								title={t('BATCH_UNLOCK')}
								disabled={!canEditStructure}
							/>
						)}
					</DialogWrapper>
				)}
				{fullySelectedNodes.length > 1 && (
					<DialogWrapper dialog={batchEditDialog} node={node}>
						{(onClick) => (
							<ContextMenuItem
								onClick={onClick}
								icon={faEdit}
								role="menuitem"
								title={t('BATCH_EDIT')}
								disabled={!canEditStructure}
							/>
						)}
					</DialogWrapper>
				)}
			</ContextMenuItems>
		)
	}
}
